import { useState, useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import FormField from "layouts/applications/wizard/components/FormField";
import CircularProgress from "@mui/material/CircularProgress";

import partCodesService from "services/part-code-service";
import partCategoryService from "services/part-category-service";
import formatPartCode from "helper/formatPartCode";

export default function GeneralInfoFields({
  changeHandler,
  part,
  error
}) {
  const [categories, setCategories] = useState([]);
  const [partCategories, setPartCategories] = useState([]);
  const [filteredPartCategories, setFilteredPartCategories] = useState([]);
  const [selectedParentCategory, setSelectedParentCategory] = useState("");
  const [selectedPartCategory, setSelectedPartCategory] = useState("");
  const [parts, setParts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [manufacturerCount, setManufacturerCount] = useState(1);

  // Fetches the categories and partCategories for the options
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const [response1, response2, response3] = await Promise.all([
          partCategoryService.getAllParentCategories(),
          partCategoryService.getAllPartCategories(),
          partCodesService.getAllParts()
        ]);
        setCategories(response1.data.attributes);
        setPartCategories(response2.data.attributes);
        setParts(response3.data.attributes);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Handles Category Change
  useEffect(() => {
    if (selectedParentCategory) {
      const filterpartcategories = partCategories.filter((partCategory) => partCategory.parentCategory === selectedParentCategory);
      setFilteredPartCategories(filterpartcategories);
    } else {
      // Empties the fields related to parentCategory if no parentCategory is selected
      setFilteredPartCategories([]);
      changeHandler({ target: { name: "unique_num", value: "" } });
    }
    setSelectedPartCategory("");
    changeHandler({ target: { name: "partCategory.parentCategory", value: selectedParentCategory } });
  }, [selectedParentCategory, partCategories]);

  // Handles Part Code change
  useEffect(() => {
    // Reset attributes everytime Part Category changes
    changeHandler({ target: { name: "partCategory.attributes", value: "" } });

    if (selectedPartCategory) {

      // Finds the smallest available unique_num of the selected part cateogry
      const filteredParts = parts.filter((part) => part.partCategory.categoryCode === selectedPartCategory.categoryCode);
      const unique_num = findAvailableNumber(filteredParts);

      // Concatenates part category and unique num into the generated UID (Part code)
      const generatedUID = formatPartCode(unique_num, selectedPartCategory.categoryCode, 1);
      // Logs all changes
      changeHandler({ target: { name: "partCategory.categoryCode", value: selectedPartCategory.categoryCode } });
      changeHandler({ target: { name: "partCategory.id", value: selectedPartCategory._id } });
      changeHandler({ target: { name: "unique_num", value: unique_num } });
      changeHandler({ target: { name: "generatedUID", value: generatedUID } });

      if (selectedPartCategory.attributes) {
        changeHandler({ target: { name: "partCategory.attributes", value: selectedPartCategory.attributes } });
        changeHandler({ target: { name: "partCategory.has_attributes", value: true } });
      }
      if (selectedPartCategory.units) {
        changeHandler({ target: { name: "partCategory.units", value: selectedPartCategory.units } });
      }
    } else {
      // Empties all related fields if no partCategory is selected
      changeHandler({ target: { name: "partCategory.categoryCode", value: "" } });
      changeHandler({ target: { name: "partCategory.id", value: "" } });
      changeHandler({ target: { name: "unique_num", value: "" } });
      changeHandler({ target: { name: "generatedUID", value: "" } });
    }
  }, [selectedPartCategory, parts]);

  // Finds the smallest available unique_num in the selected part category
  const findAvailableNumber = (partCodes) => {
    // Sort part codes by unique_num
    const sorted = partCodes.map(p => p.unique_num).sort((a, b) => a - b);

    // Start checking from 1 upwards
    let current = 1;
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i] === current) {
        current++;
      } else if (sorted[i] > current) {
        // Found a gap
        break;
      }
    }
    return current;
  }

  const handleAddManufacturer = () => {
    setManufacturerCount(manufacturerCount + 1);
  }

  const handleRemoveManufacturer = (index) => {
    const currentMpn = part.mpn || {};
    const maxIndex = manufacturerCount - 1;

    const reorderedMpn = {};
    for (let i = 0, j = 0; i <= maxIndex; i++) {
      if (i === index) continue;
      reorderedMpn[j] = currentMpn[i] || "";
      j++;
    }

    changeHandler({ target: { name: "mpn", value: reorderedMpn } });

    const preferred = Number(part.preferred_mpn ?? 0);
    if (preferred === index) {
      changeHandler({ target: { name: "preferred_mpn", value: "0" } });
    } else if (preferred > index) {
      changeHandler({ target: { name: "preferred_mpn", value: String(preferred - 1) } });
    }

    setManufacturerCount(manufacturerCount - 1);
  }

  const commonProps = {
    display: "flex",
    flexDirection: "column",
    fullWidth: true,
    marginBottom: "1rem",
    marginTop: "1rem"
  };

  return (
    <MDBox>
      {isLoading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" height="80px">
          <CircularProgress />
        </MDBox>
      ) : (
        <Grid container spacing={2}>

          {/* Parent Category */}
          <Grid item xs={4} md={4}>
            <MDBox {...commonProps}>
              <Autocomplete
                defaultValue=""
                options={categories}
                getOptionLabel={(option) => option}
                value={selectedParentCategory ?? ""}
                error={error.parentCategory}
                onChange={(event, newValue) => {
                  setSelectedParentCategory(newValue);
                }}
                renderInput={(params) => (
                  <FormField {...params} label="Parent Category" InputLabelProps={{ shrink: true }} />
                )}
              />
              {error.parentCategory && (
                <MDTypography variant="caption" color="error" fontWeight="light">
                  {error.parentCategory}
                </MDTypography>
              )}
            </MDBox>
          </Grid>

          {/* Part Category*/}
          <Grid item xs={4} md={4}>
            <MDBox {...commonProps}>
              <Autocomplete
                defaultValue=""
                options={filteredPartCategories}
                error={error.categoryCode}
                getOptionLabel={(option) => (option ? option.categoryCode : "")}
                value={selectedPartCategory ?? ""}
                onChange={(event, newValue) => {
                  setSelectedPartCategory(newValue);
                }}
                renderInput={(params) => (
                  <FormField {...params} label="Part Category" InputLabelProps={{ shrink: true }} />
                )}
              />
              {error.categoryCode && (
                <MDTypography variant="caption" color="error" fontWeight="light">
                  {error.categoryCode}
                </MDTypography>
              )}
            </MDBox>
          </Grid>

          {/* Generated Part Code */}
          <Grid item xs={4} md={4}>
            <MDBox {...commonProps}>
              <MDInput
                label="Generated Part Code"
                placeholder=" "
                name="generatedUid"
                value={part.generatedUID}
                fullWidth
                disabled
                inputProps={{
                  autoComplete: "examples",
                  form: {
                    autoComplete: "off",
                  },
                }}
              />
            </MDBox>
          </Grid>
        </Grid>)}

      {/* Description */}
      <MDInput
        label="Description"
        placeholder=" "
        name="description"
        value={part.description}
        onChange={changeHandler}
        error={error.description}
        fullWidth
        disabled
        inputProps={{
          autoComplete: "description",
          form: {
            autoComplete: "off",
          },
        }}
      />
      {error.description && (
        <MDTypography variant="caption" color="error" fontWeight="light">
          {error.description}
        </MDTypography>
      )}

      <MDBox mt={1}>
        <MDTypography variant="h9" mt={1}>
          Manufacturer Information :
        </MDTypography>
      </MDBox>

      <Grid container spacing={3} mt={0.5}>

        {/* Manufacturer Name*/}
        <Grid item xs={12} sm={12}>
          <MDInput
            label="Manufacturer"
            placeholder=" "
            name="manufacturer"
            value={part.manufacturer}
            error={error.manufacturer}
            onChange={changeHandler}
            fullWidth
            inputProps={{
              autoComplete: "manufacturer",
              form: {
                autoComplete: "off",
              },
            }}
          />
          {error.manufacturer && (
            <MDTypography variant="caption" color="error" fontWeight="light">
              {error.manufacturer}
            </MDTypography>
          )}
        </Grid>
        
        <MDBox mt={1} ml={3}>
          <MDTypography variant="h6" fontWeight="light">
            Preferred Manufacturer Part Number :
          </MDTypography>
        </MDBox>

        {/* Manufacturer Part Numbers*/}
        <Grid item xs={12} sm={12}>
          <MDBox>
            <RadioGroup
              value={part.preferred_mpn || "0"}
              onChange={(event) => changeHandler({
                target: {
                  name: "preferred_mpn",
                  value: event.target.value
                }
              })}
            >
              {Array.from({ length: manufacturerCount }).map((_, index) => (
                <Grid container spacing={1} key={index} alignItems="center" sx={{ mb: 2 }}>
                  <Grid item xs="auto">
                    <FormControlLabel
                      value={index.toString()}
                      control={<Radio />}
                      label=""
                    />
                  </Grid>
                  <Grid item xs={true} sm={true}>
                    <MDInput
                      label={`Manufacturer Part Number ${index + 1}`}
                      placeholder=" "
                      name={`mpn.${index}`}
                      value={part.mpn?.[index] || ""}
                      onChange={changeHandler}
                      error={error[`mpn${index}`]}
                      fullWidth
                      inputProps={{
                        autoComplete: "manufacturer_part_number",
                        form: {
                          autoComplete: "off",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs="auto">
                    {index === manufacturerCount - 1 ? (
                      <IconButton
                        onClick={handleAddManufacturer}
                        size="small"
                        color="success"
                      >
                        <AddIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => handleRemoveManufacturer(index)}
                        size="small"
                        color="error"
                      >
                        <RemoveIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </RadioGroup>
          </MDBox>
          {error.manufacturer_part_number && (
            <MDTypography variant="caption" color="error" fontWeight="light">
              {error.manufacturer_part_number}
            </MDTypography>
          )}
        </Grid>
      </Grid>

      <MDBox mt={1}>
        <MDTypography variant="h9" mt={1}>
          Vendors Information :
        </MDTypography>
        <MDTypography variant="h6" fontWeight="light">
          Preferred <br /> Vendor :
        </MDTypography>
      </MDBox>

          
      {/* Preferred Vendor Selector */}
      <Grid container spacing={1} mt={0.5}>

        <Grid item xs={12} sm={1.5}>
          <MDBox mt={1} ml={3}>
            <RadioGroup
              defaultValue="1"
              onChange={(event) => changeHandler({
                target: {
                  name: "preferred_vendor",
                  value: event.target.value
                }
              })}
            >
              <FormControlLabel value="1" control={<Radio />} label="1" />
              <FormControlLabel value="2" control={<Radio />} label="2" />

            </RadioGroup>
          </MDBox>
        </Grid>

        {/* Vendor Name*/}
        <Grid item xs={12} sm={5.5}>
          <MDInput
            label="Vendor 1"
            placeholder=" "
            name="vendors.vendor-1"
            value={part.vendors["vendor-1"]}
            onChange={changeHandler}
            error={error.vendor1}
            fullWidth
            inputProps={{
              autoComplete: "vendor",
              form: {
                autoComplete: "off",
              },
            }}
          />
          {error.vendor1 && (
            <MDTypography variant="caption" color="error" fontWeight="light">
              {error.vendor1}
            </MDTypography>
          )}
          <MDBox mt={1}>
            <MDInput
              label="Vendor 2"
              placeholder=" "
              name="vendors.vendor-2"
              value={part.vendors["vendor-2"]}
              onChange={changeHandler}
              error={error.vendor2}
              fullWidth
              inputProps={{
                autoComplete: "vendor",
                form: {
                  autoComplete: "off",
                },
              }}
            />
            {error.vendor2 && (
              <MDTypography variant="caption" color="error" fontWeight="light">
                {error.vendor2}
              </MDTypography>
            )}
          </MDBox>
        </Grid>

        {/* Vendor Part Number*/}
        <Grid item xs={12} sm={5}>
          <MDInput
            label="Vendor Link 1"
            placeholder=" "
            name="vendors.link-1"
            value={part.vendors["link-1"]}
            onChange={changeHandler}
            error={error.link1}
            fullWidth
            inputProps={{
              autoComplete: "vendor",
              form: {
                autoComplete: "off",
              },
            }}
          />
          {error.link1 && (
            <MDTypography variant="caption" color="error" fontWeight="light">
              {error.link1}
            </MDTypography>
          )}

          <MDBox mt={1}>
            <MDInput
              label="Vendor Link 2"
              placeholder=" "
              name="vendors.link-2"
              value={part.vendors["link-2"]}
              onChange={changeHandler}
              error={error.link2}
              fullWidth
              inputProps={{
                autoComplete: "vendor",
                form: {
                  autoComplete: "off",
                },
              }}
            />
            {error.link2 && (
              <MDTypography variant="caption" color="error" fontWeight="light">
                {error.link2}
              </MDTypography>
            )}
          </MDBox>

        </Grid>
      </Grid>
      <MDBox mt={1}>
        <MDInput
          label="Vendor Part Number"
          placeholder=" "
          name="vendor_part_number"
          value={part.vendor_part_number}
          onChange={changeHandler}
          error={error.vendor_part_number}
          fullWidth
          inputProps={{
            autoComplete: "vendor_part_number",
            form: {
              autoComplete: "off",
            },
          }}
        />
        {error.vendor_part_number && (
          <MDTypography variant="caption" color="error" fontWeight="light">
            {error.vendor_part_number}
          </MDTypography>
        )}
      </MDBox>
    </MDBox>
  )
}